<template>
    <div class="nevs-content">
        <!-- ── Trenutno stanje ────────────────────────────────────────────── -->
        <NevsCard>
            <div class="overview-kpis">
                <div class="overview-kpi is-active">
                    <div class="overview-kpi-icon"><i class="fa-solid fa-user-check"></i></div>
                    <div class="overview-kpi-body">
                        <div class="overview-kpi-value">{{ general.active_members }}</div>
                        <div class="overview-kpi-label">{{ $LANG.Get('statistics.activeMembers') }}</div>
                    </div>
                    <span class="overview-kpi-list" :title="$LANG.Get('tooltips.showRecords')"
                          @click="openRecords('active', $LANG.Get('statistics.activeMembers'))">
                        <i class="fa-solid fa-list-ul"></i>
                    </span>
                </div>
                <div class="overview-kpi is-lapsed">
                    <div class="overview-kpi-icon"><i class="fa-solid fa-user-xmark"></i></div>
                    <div class="overview-kpi-body">
                        <div class="overview-kpi-value">{{ general.lapsed_members }}</div>
                        <div class="overview-kpi-label">{{ $LANG.Get('statistics.lapsedMembers') }}</div>
                        <div class="overview-kpi-hint">{{ $LANG.Get('statistics.lapsedMembersHint').replace('%d', general.lapsed_days) }}</div>
                    </div>
                    <span class="overview-kpi-list" :title="$LANG.Get('tooltips.showRecords')"
                          @click="openRecords('lapsed', $LANG.Get('statistics.lapsedMembers'))">
                        <i class="fa-solid fa-list-ul"></i>
                    </span>
                </div>
                <div class="overview-kpi is-absent">
                    <div class="overview-kpi-icon"><i class="fa-solid fa-person-walking-arrow-loop-left"></i></div>
                    <div class="overview-kpi-body">
                        <div class="overview-kpi-value">{{ general.absent_members }}</div>
                        <div class="overview-kpi-label">{{ $LANG.Get('statistics.absentMembers') }}</div>
                        <div class="overview-kpi-hint">{{ $LANG.Get('statistics.absentMembersHint').replace('%d', general.absent_days) }}</div>
                    </div>
                    <span class="overview-kpi-list" :title="$LANG.Get('tooltips.showRecords')"
                          @click="openRecords('absent', $LANG.Get('statistics.absentMembers'))">
                        <i class="fa-solid fa-list-ul"></i>
                    </span>
                </div>
                <div class="overview-kpi is-expiring">
                    <div class="overview-kpi-icon"><i class="fa-solid fa-hourglass-half"></i></div>
                    <div class="overview-kpi-body">
                        <div class="overview-kpi-value">{{ general.expiring_members }}</div>
                        <div class="overview-kpi-label">{{ $LANG.Get('statistics.expiringMembers') }}</div>
                        <div class="overview-kpi-hint">{{ $LANG.Get('statistics.expiringMembersHint').replace('%d', general.expiring_days) }}</div>
                    </div>
                    <span class="overview-kpi-list" :title="$LANG.Get('tooltips.showRecords')"
                          @click="openRecords('expiring', $LANG.Get('statistics.expiringMembers'))">
                        <i class="fa-solid fa-list-ul"></i>
                    </span>
                </div>
            </div>
        </NevsCard>

        <!-- ── Mjesec ─────────────────────────────────────────────────────── -->
        <NevsCard class="overview-card">
            <div class="overview-section-head">
                <div class="overview-pickers">
                    <NevsSelect v-model="selectedMonth" :options="monthOptions" width="150px"
                                :label="$LANG.Get('statistics.month') + ':'"></NevsSelect>
                    <NevsSelect v-model="selectedMonthYear" :options="yearOptions" width="110px"
                                :label="$LANG.Get('statistics.year') + ':'"></NevsSelect>
                </div>
            </div>
            <div class="overview-month-grid">
                <div class="overview-kpi is-new">
                    <div class="overview-kpi-icon"><i class="fa-solid fa-user-plus"></i></div>
                    <div class="overview-kpi-body">
                        <div class="overview-kpi-value">{{ monthly.new_members }}</div>
                        <div class="overview-kpi-label">{{ $LANG.Get('statistics.newMembers') }}</div>
                    </div>
                </div>
                <div class="overview-kpi is-invoiced">
                    <div class="overview-kpi-icon"><i class="fa-solid fa-file-invoice"></i></div>
                    <div class="overview-kpi-body">
                        <div class="overview-kpi-value overview-kpi-value-money">{{ monthly.invoiced.display }}</div>
                        <div class="overview-kpi-label">{{ $LANG.Get('statistics.invoicedTotal') }}</div>
                    </div>
                </div>
                <div class="overview-donut">
                    <NevsDonut :slices="monthly.memberships.slices"
                               :title="$LANG.Get('statistics.membershipsShare')"
                               :center-value="monthly.memberships.total.display"
                               :center-label="$LANG.Get('statistics.total')"
                               :empty-text="$LANG.Get('labels.noStatisticsData')"></NevsDonut>
                </div>
                <div class="overview-donut">
                    <NevsDonut :slices="monthly.products.slices"
                               :title="$LANG.Get('statistics.productsShare')"
                               :center-value="monthly.products.total.display"
                               :center-label="$LANG.Get('statistics.total')"
                               :empty-text="$LANG.Get('labels.noStatisticsData')"></NevsDonut>
                </div>
            </div>
        </NevsCard>

        <!-- ── Godina ─────────────────────────────────────────────────────── -->
        <NevsCard class="overview-card">
            <div class="overview-section-head">
                <div class="overview-pickers">
                    <div class="overview-total">
                        <span class="overview-total-label">{{ $LANG.Get('statistics.yearTotal') }}:</span>
                        <span class="overview-total-value">{{ yearly.total.display }}</span>
                    </div>
                    <NevsSelect v-model="selectedYear" :options="yearOptions" width="110px"
                                :label="$LANG.Get('statistics.year') + ':'"></NevsSelect>
                </div>
            </div>
            <NevsChart :labels="monthNames" :values="yearly.values"
                       :empty-text="$LANG.Get('labels.noStatisticsData')" :height="320"></NevsChart>
        </NevsCard>

        <ModalOverviewPersons :show="recordsPopup.show" :type="recordsPopup.type" :title="recordsPopup.title"
                              @close="recordsPopup.show = false"></ModalOverviewPersons>
    </div>
</template>

<script>
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsChart from "@/components/nevs/NevsChart.vue";
import NevsDonut from "@/components/nevs/NevsDonut.vue";
import ModalOverviewPersons from "@/components/modals/ModalOverviewPersons.vue";
import moment from "moment";

export default {
    name: "StatisticsOverview",
    components: {ModalOverviewPersons, NevsDonut, NevsChart, NevsSelect, NevsCard},
    data() {
        let now = moment();
        return {
            selectedMonth: parseInt(now.format('M')),
            selectedMonthYear: parseInt(now.format('YYYY')),
            selectedYear: parseInt(now.format('YYYY')),
            // Empty shapes so the screen renders its layout before the first response arrives.
            general: {
                active_members: 0,
                lapsed_members: 0,
                absent_members: 0,
                expiring_members: 0,
                lapsed_days: 30,
                absent_days: 14,
                expiring_days: 7
            },
            monthly: this.emptyMonthly(),
            yearly: {
                values: [],
                total: {amount: 0, display: ''}
            },
            // Which tile's list is open. The title travels with it so the popup is headed by the same wording
            // the user clicked on.
            recordsPopup: {show: false, type: '', title: ''}
        }
    },
    computed: {
        // Month names in the user's locale. Read off locale-bound clones rather than moment.locale(), so
        // asking for them never changes the global locale as a side effect.
        monthNames() {
            let locale = this.$store.state.locale;
            let names = [];
            for (let month = 0; month < 12; month++) {
                let name = moment().locale(locale).month(month).format('MMMM');
                names.push(name.charAt(0).toUpperCase() + name.slice(1));
            }
            return names;
        },
        monthOptions() {
            return this.monthNames.map((name, index) => ({value: index + 1, label: name}));
        },
        // Ten years back plus the current one; anything older is out of scope for an overview screen.
        yearOptions() {
            let current = parseInt(moment().format('YYYY'));
            let options = [];
            for (let year = current; year >= current - 10; year--) {
                options.push({value: year, label: String(year)});
            }
            return options;
        },
        monthParameter() {
            return this.selectedMonthYear + '-' + String(this.selectedMonth).padStart(2, '0');
        }
    },
    watch: {
        monthParameter() {
            this.loadMonthly();
        },
        selectedYear() {
            this.loadYearly();
        }
    },
    methods: {
        emptyMonthly() {
            let zero = {amount: 0, display: ''};
            return {
                new_members: 0,
                invoiced: zero,
                memberships: {total: zero, slices: []},
                products: {total: zero, slices: []}
            };
        },
        openRecords(type, title) {
            this.recordsPopup.type = type;
            this.recordsPopup.title = title;
            this.recordsPopup.show = true;
        },
        loadGeneral() {
            let vm = this;
            this.$API.APICall('get', 'overview-statistics/general', {}, (data, success) => {
                if (success) {
                    vm.general = data;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        loadMonthly() {
            let vm = this;
            this.$API.APICall('get', 'overview-statistics/monthly', {month: this.monthParameter}, (data, success) => {
                if (success) {
                    vm.monthly = data;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        loadYearly() {
            let vm = this;
            this.$API.APICall('get', 'overview-statistics/yearly', {year: this.selectedYear}, (data, success) => {
                if (success) {
                    vm.yearly = data;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'statistics');
        this.$store.commit('selectSubMenu', 'overviewStatistics');
        this.$store.commit('setBreadcrumbs', [
            {label: this.$LANG.Get('modules.statistics'), link: null},
            {label: this.$LANG.Get('modules.overviewStatistics'), link: null}
        ]);
        this.loadGeneral();
        this.loadMonthly();
        this.loadYearly();
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/palette' as p;

.overview-card {
    margin-top: 16px;
}

.overview-section-head {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    padding: 5px 5px 12px;
}

.overview-pickers {
    display: flex;
    /* Centred rather than bottom-aligned: the selects carry a label, so their box is much taller than the
       total beside them and any bottom alignment would need a magic offset to look right. */
    align-items: center;
    gap: 6px;
}

.overview-total {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 999px;
    background: p.$peach-wash;
    border: 1px solid p.$sand-300;
}

.overview-total-label {
    color: p.$ink-muted;
    font-size: 13px;
}

.overview-total-value {
    font-weight: 700;
    color: p.$ink-strong;
}

/* ── KPI tiles ─────────────────────────────────────────────────────────── */

.overview-kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 12px;
    padding: 0 5px 5px;
}

.overview-kpi {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 10px;
    background: p.$sand-50;
    border: 1px solid p.$sand-300;
    /* The accent stripe is what tells the tiles apart at a glance. */
    border-left: 4px solid p.$sand-400;
}

.overview-kpi-icon {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: p.$white;
    color: p.$ink-muted;
    font-size: 17px;
}

.overview-kpi-body {
    min-width: 0;
}

/* Pushed to the far edge of the tile, away from the number it belongs to. Faint until pointed at: it is an
   extra, and four of them competing with the figures would make the row look busy. */
.overview-kpi-list {
    flex: 0 0 auto;
    margin-left: auto;
    align-self: flex-start;
    padding: 4px;
    cursor: pointer;
    color: p.$ink-faint;
    font-size: 15px;
}

.overview-kpi-list:hover {
    color: p.$ink-strong;
}

.overview-kpi-value {
    font-size: 30px;
    line-height: 1.1;
    font-weight: 700;
    color: p.$ink-strong;
}

.overview-kpi-value-money {
    font-size: 24px;
}

.overview-kpi-label {
    margin-top: 4px;
    color: p.$ink;
    font-weight: 600;
    font-size: 13px;
}

.overview-kpi-hint {
    margin-top: 2px;
    color: p.$ink-faint;
    font-size: 12px;
}

.overview-kpi.is-active {
    border-left-color: p.$success;

    .overview-kpi-icon {
        color: p.$success;
    }
}

.overview-kpi.is-lapsed {
    border-left-color: p.$danger;

    .overview-kpi-icon {
        color: p.$danger;
    }
}

.overview-kpi.is-absent {
    border-left-color: p.$warning;

    .overview-kpi-icon {
        color: p.$warning;
    }
}

.overview-kpi.is-expiring {
    border-left-color: p.$orange;

    .overview-kpi-icon {
        color: p.$orange;
    }
}

.overview-kpi.is-new {
    border-left-color: p.$success;

    .overview-kpi-icon {
        color: p.$success;
    }
}

.overview-kpi.is-invoiced {
    border-left-color: p.$orange;

    .overview-kpi-icon {
        color: p.$orange;
    }
}

/* ── Monthly block ─────────────────────────────────────────────────────── */

.overview-month-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    padding: 0 5px 5px;
}

.overview-donut {
    padding: 16px;
    border-radius: 10px;
    background: p.$sand-50;
    border: 1px solid p.$sand-300;
    min-width: 0;
}

@media (max-width: 900px) {
    .overview-month-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
